// src/services/assignment.service.ts
import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import {
  WorkloadAssignmentLog,
  CreateAssignmentLogDto,
  AssignmentLogFilters,
  AssignmentAction,
  AssignmentChange,
  getActionLabel,
  generateActionDescription,
} from '../types/assignment.types';
import { useAuth } from '../contexts/AuthContext';

const COLLECTION_NAME = 'workloadAssignmentLogs';


export async function logAction(
  data: CreateAssignmentLogDto & { performedBy?: string; performedByName?: string }
): Promise<WorkloadAssignmentLog> {
  // Get current user if not provided
  let performedBy = data.performedBy || 'system';
  let performedByName = data.performedByName || 'System';

  // Try to get from localStorage if available (for sync context)
  if (typeof window !== 'undefined' && !data.performedBy) {
    try {
      const stored = localStorage.getItem('currentUser');
      if (stored) {
        const user = JSON.parse(stored);
        performedBy = user.uid || performedBy;
        performedByName = user.displayName || user.email || performedByName;
      }
    } catch {
      // Ignore parsing errors
    }
  }

  const now = Timestamp.now();

  const logData = {
    workloadId: data.workloadId,
    workloadName: data.workloadName,
    projectId: data.projectId,
    action: data.action,
    description: data.description || generateActionDescription(data.action, data.changes, data.metadata),
    performedBy,
    performedByName,
    performedAt: now,
    changes: data.changes,
    batchId: data.batchId || null,
    batchSize: data.batchSize || null,
    metadata: data.metadata || null,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), logData);

  return {
    id: docRef.id,
    ...logData,
  } as WorkloadAssignmentLog;
}

export async function logBatchAction(
  projectId: string,
  action: AssignmentAction,
  workloads: Array<{ id: string; name: string }>,
  changes: AssignmentChange[],
  performedBy: string,
  performedByName: string,
  metadata?: Record<string, any>
): Promise<string> {
  const batchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Create a single log entry for the batch
  await logAction({
    workloadId: 'batch',
    workloadName: `Batch operation on ${workloads.length} workloads`,
    projectId,
    action: 'batch_update',
    description: `${getActionLabel(action)} applied to ${workloads.length} workloads`,
    changes,
    performedBy,
    performedByName,
    batchId,
    batchSize: workloads.length,
    metadata: {
      ...metadata,
      affectedWorkloads: workloads.map((w) => ({ id: w.id, name: w.name })),
      originalAction: action,
    },
  });

  return batchId;
}


export async function getLog(logId: string): Promise<WorkloadAssignmentLog | null> {
  const docRef = doc(db, COLLECTION_NAME, logId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as WorkloadAssignmentLog;
}

export async function getWorkloadLogs(
  workloadId: string,
  maxResults: number = 50
): Promise<WorkloadAssignmentLog[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('workloadId', '==', workloadId),
    orderBy('performedAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WorkloadAssignmentLog[];
}

export async function getProjectLogs(
  projectId: string,
  filters?: AssignmentLogFilters,
  maxResults: number = 100
): Promise<WorkloadAssignmentLog[]> {
  const constraints: QueryConstraint[] = [
    where('projectId', '==', projectId),
    orderBy('performedAt', 'desc'),
    limit(maxResults),
  ];

  if (filters?.action && filters.action.length > 0) {
    constraints.push(where('action', 'in', filters.action));
  }

  if (filters?.performedBy) {
    constraints.push(where('performedBy', '==', filters.performedBy));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  let logs = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WorkloadAssignmentLog[];

  // Filter by date range on client side (Firestore limitation with multiple inequalities)
  if (filters?.dateRange) {
    const startTime = filters.dateRange.start.getTime();
    const endTime = filters.dateRange.end.getTime();
    logs = logs.filter((log) => {
      const logTime = log.performedAt.toDate().getTime();
      return logTime >= startTime && logTime <= endTime;
    });
  }

  // Filter by workloadId on client side if specified
  if (filters?.workloadId) {
    logs = logs.filter((log) => log.workloadId === filters.workloadId);
  }

  return logs;
}

export async function getLogsPaginated(
  projectId: string,
  pageSize: number = 20,
  lastDoc?: DocumentSnapshot,
  filters?: AssignmentLogFilters
): Promise<{ logs: WorkloadAssignmentLog[]; lastDoc: DocumentSnapshot | null }> {
  const constraints: QueryConstraint[] = [
    where('projectId', '==', projectId),
    orderBy('performedAt', 'desc'),
    limit(pageSize),
  ];

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  if (filters?.action && filters.action.length > 0) {
    constraints.push(where('action', 'in', filters.action));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  const logs = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WorkloadAssignmentLog[];

  const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

  return { logs, lastDoc: newLastDoc };
}

export async function getBatchLogs(batchId: string): Promise<WorkloadAssignmentLog[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('batchId', '==', batchId),
    orderBy('performedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WorkloadAssignmentLog[];
}

export async function getUserActivityLogs(
  userId: string,
  projectId?: string,
  maxResults: number = 50
): Promise<WorkloadAssignmentLog[]> {
  const constraints: QueryConstraint[] = [
    where('performedBy', '==', userId),
    orderBy('performedAt', 'desc'),
    limit(maxResults),
  ];

  if (projectId) {
    constraints.push(where('projectId', '==', projectId));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WorkloadAssignmentLog[];
}


export async function getLogStats(
  projectId: string,
  dateRange?: { start: Date; end: Date }
): Promise<{
  total: number;
  byAction: Record<AssignmentAction, number>;
  byUser: Record<string, { count: number; name: string }>;
  recentActivity: WorkloadAssignmentLog[];
}> {
  const logs = await getProjectLogs(projectId, { dateRange }, 500);

  const stats: {
    total: number;
    byAction: Record<AssignmentAction, number>;
    byUser: Record<string, { count: number; name: string }>;
    recentActivity: WorkloadAssignmentLog[];
  } = {
    total: logs.length,
    byAction: {
      created: 0,
      assigned: 0,
      unassigned: 0,
      reassigned: 0,
      status_changed: 0,
      survey_linked: 0,
      survey_unlinked: 0,
      addresses_added: 0,
      addresses_removed: 0,
      addresses_imported: 0,
      territory_updated: 0,
      batch_update: 0,
      archived: 0,
      restored: 0,
      deleted: 0,
    },
    byUser: {},
    recentActivity: logs.slice(0, 10),
  };

  logs.forEach((log) => {
    // Count by action
    stats.byAction[log.action]++;

    // Count by user
    if (!stats.byUser[log.performedBy]) {
      stats.byUser[log.performedBy] = { count: 0, name: log.performedByName };
    }
    stats.byUser[log.performedBy].count++;
  });

  return stats;
}


export async function exportLogs(
  projectId: string,
  filters?: AssignmentLogFilters
): Promise<string> {
  const logs = await getProjectLogs(projectId, filters, 10000);

  // Generate CSV
  const headers = [
    'Timestamp',
    'Workload Name',
    'Action',
    'Description',
    'Performed By',
    'Batch ID',
  ];

  const rows = logs.map((log) => [
    log.performedAt.toDate().toISOString(),
    log.workloadName,
    getActionLabel(log.action),
    log.description,
    log.performedByName,
    log.batchId || '',
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  return csvContent;
}

// Export service object
export const assignmentService = {
  logAction,
  logBatchAction,
  get: getLog,
  getWorkloadLogs,
  getProjectLogs,
  getPaginated: getLogsPaginated,
  getBatchLogs,
  getUserActivityLogs,
  getStats: getLogStats,
  export: exportLogs,
};

export default assignmentService;
